import type { ComponentType, KeyboardEvent, ReactNode, SVGProps } from 'react';
import { cn } from '@c2fo/liquidity';

export interface KpiSpotlightBannerShellProps {
  containerClassName: string;
  accentClassName: string;
  textClassName: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconClassName: string;
  title: ReactNode;
  description: ReactNode;
  action?: {
    text: ReactNode;
    onClick: () => void;
  };
}

const isActivationKey = (key: string) => key === 'Enter' || key === ' ';

/**
 * Shared visual chrome for every Invoice Central FinanceIQ banner state — each KPI spotlight and
 * the static fallback promo alike: icon + title row, one flowing description line, a thick left
 * accent bar, and (when `action` is given) the whole row acts as the CTA — not just a small button
 * in the corner — with the action's text rendered as a plain label on the right. Kept purely
 * presentational (no data fetching, no per-KPI knowledge) so every state renders identically
 * regardless of what triggered it.
 */
const KpiSpotlightBannerShell = ({
  containerClassName,
  accentClassName,
  textClassName,
  icon: Icon,
  iconClassName,
  title,
  description,
  action,
}: KpiSpotlightBannerShellProps) => (
  <div
    aria-atomic="true"
    aria-live="assertive"
    role={action ? 'button' : 'alert'}
    tabIndex={action ? 0 : undefined}
    onClick={action?.onClick}
    onKeyDown={
      action
        ? (event: KeyboardEvent<HTMLDivElement>) => {
            if (isActivationKey(event.key)) {
              event.preventDefault();
              action.onClick();
            }
          }
        : undefined
    }
    className={cn(
      'relative flex items-center gap-4 overflow-hidden rounded-md border p-4',
      containerClassName,
      textClassName,
      action && 'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2'
    )}
  >
    {/* Thick left accent stripe — border-radius-clipped via the container's overflow-hidden */}
    <span className={cn('absolute inset-y-0 left-0 w-1.5', accentClassName)} />
    {/* A sibling of the action label in the outer items-center row, so the label centers
        against this whole block's height rather than just the title line. */}
    <div className="flex min-w-0 flex-1 items-start gap-3">
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', iconClassName)} />
      <div className="min-w-0 flex-1">
        <div className="font-bold">{title}</div>
        {/* One flowing line, sized like Alert's own description — matches title+description
            height instead of stacking a third, larger-type row underneath. Tight gap since the
            bullet separators between chunks (added by callers) carry the visual separation.
            items-center, not items-baseline: one chunk's first flex item is an icon (a replaced
            element with no text baseline), so a flex container's baseline falls back to that
            icon's bottom edge instead of the surrounding text's baseline — that mismatch against
            the plain-text siblings caused a subtle height/position shift once the icon rendered. */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-sm">{description}</div>
      </div>
    </div>
    {/* Plain label, not a nested interactive element — the whole row above already carries the click. */}
    {action && <span className="flex shrink-0 items-center gap-2 rounded-full px-2 py-1 font-bold">{action.text}</span>}
  </div>
);

export default KpiSpotlightBannerShell;
